import type { CSSProperties } from 'react'
import { useEffect, useState } from 'react'
import { useI18n } from '../i18n/context'
import { QrCodeImage } from './QrCodeImage'

interface QrIntroFrameProps {
  text: string
  label: string
  size?: number
  className?: string
  style?: CSSProperties
  expandable?: boolean
  quietZone?: boolean
}

export function QrIntroFrame({ text, label, size = 200, className = '', style, expandable = false, quietZone = false }: QrIntroFrameProps) {
  const { t } = useI18n()
  const [expanded, setExpanded] = useState(false)
  const toggleExpanded = () => {
    if (expandable) setExpanded((current) => !current)
  }

  useEffect(() => {
    if (!expanded) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false)
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [expanded])
  const frameClassName = `qr-intro-code-frame ${expanded ? 'qr-detail-code-frame-expanded' : ''} ${expandable ? 'qr-detail-code-frame-expandable' : ''} ${className}`

  const content = (
    <>
      <span className="qr-intro-finder qr-intro-finder-top-left" aria-hidden="true" />
      <span className="qr-intro-finder qr-intro-finder-top-right" aria-hidden="true" />
      <span className="qr-intro-finder qr-intro-finder-bottom-left" aria-hidden="true" />
      <QrCodeImage text={text} size={size} margin={quietZone || expanded ? 4 : 0} className="qr-intro-code pointer-events-none h-full w-full" alt={label} loading="lazy" />
    </>
  )

  if (!expandable) {
    return (
      <span className={frameClassName} style={style}>
        {content}
      </span>
    )
  }

  return (
    <>
      {expanded && (
        <button
          type="button"
          className="qr-detail-code-backdrop"
          aria-label={`${t('default-close')}: ${label}`}
          onClick={() => setExpanded(false)}
        />
      )}
      <button
        type="button"
        className={frameClassName}
        style={style}
        aria-label={t(expanded ? 'default-shrink-code' : 'default-enlarge-code', { name: label })}
        aria-pressed={expanded}
        onClick={toggleExpanded}
      >
        {content}
      </button>
    </>
  )
}
